import { useCallback, useEffect, useState } from "react";

import Button from "../common/Button";
import {
  verifyDeveloperPassword,
  listApiKeys,
  createApiKey,
  revokeApiKey,
  getQueueSettings,
  updateQueueSettings,
} from "../../services/developerApi";
import { normalizeApiError } from "../../utils/errors";

const QUEUE_FIELDS = [
  { key: "max_concurrent_per_client", label: "Max concurrent calls / client", min: 1, max: 100 },
  { key: "max_concurrent_per_campaign", label: "Max concurrent calls / campaign", min: 1, max: 100 },
  { key: "batch_size", label: "Worker batch size", min: 1, max: 100 },
  { key: "poll_seconds", label: "Worker poll interval (s)", min: 5, max: 3600 },
  { key: "lock_ttl_seconds", label: "Claim lock TTL (s)", min: 30, max: 3600 },
];

export default function DeveloperPanel({ showToast }) {
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [unlockError, setUnlockError] = useState("");

  const [keys, setKeys] = useState([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);

  const [queue, setQueue] = useState(null);
  const [queueBusy, setQueueBusy] = useState(false);

  const notify = useCallback(
    (message, isError = false, ms = 4000) => {
      if (typeof showToast === "function") showToast(message, isError, ms);
    },
    [showToast]
  );

  const loadKeys = useCallback(async () => {
    setKeysLoading(true);
    try {
      setKeys(await listApiKeys());
    } catch (error) {
      const n = normalizeApiError(error);
      if (!n.isAbort) notify(n.message, true, 5000);
    } finally {
      setKeysLoading(false);
    }
  }, [notify]);

  const loadQueue = useCallback(async () => {
    try {
      setQueue(await getQueueSettings());
    } catch (error) {
      const n = normalizeApiError(error);
      if (!n.isAbort) notify(n.message, true, 5000);
    }
  }, [notify]);

  useEffect(() => {
    if (unlocked) {
      loadKeys().catch(() => undefined);
      loadQueue().catch(() => undefined);
    }
  }, [unlocked, loadKeys, loadQueue]);

  const handleUnlock = async (event) => {
    event?.preventDefault?.();
    if (!password) return;
    setVerifying(true);
    setUnlockError("");
    try {
      await verifyDeveloperPassword(password);
      setPassword("");
      setUnlocked(true);
    } catch (error) {
      setUnlockError(normalizeApiError(error).message || "Incorrect password");
    } finally {
      setVerifying(false);
    }
  };

  const handleCreate = async (event) => {
    event?.preventDefault?.();
    const name = newKeyName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const created = await createApiKey(name);
      setCreatedKey(created);
      setNewKeyName("");
      await loadKeys();
      notify("API key created. Copy it now — it won't be shown again.", false, 6000);
    } catch (error) {
      const n = normalizeApiError(error);
      if (!n.isAbort) notify(n.message, true, 5000);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id) => {
    try {
      await revokeApiKey(id);
      await loadKeys();
      notify("API key revoked.", false, 4000);
    } catch (error) {
      const n = normalizeApiError(error);
      if (!n.isAbort) notify(n.message, true, 5000);
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      notify("Copied to clipboard.", false, 2500);
    } catch (error) {
      notify("Copy failed — select the key and copy manually.", true, 3000);
    }
  };

  const handleSaveQueue = async () => {
    if (!queue) return;
    setQueueBusy(true);
    try {
      const saved = await updateQueueSettings({
        max_concurrent_per_client: Number(queue.max_concurrent_per_client),
        max_concurrent_per_campaign: Number(queue.max_concurrent_per_campaign),
        batch_size: Number(queue.batch_size),
        poll_seconds: Number(queue.poll_seconds),
        lock_ttl_seconds: Number(queue.lock_ttl_seconds),
        dry_run: Boolean(queue.dry_run),
      });
      setQueue(saved);
      notify("Queue settings saved.", false, 4000);
    } catch (error) {
      const n = normalizeApiError(error);
      if (!n.isAbort) notify(n.message, true, 5000);
    } finally {
      setQueueBusy(false);
    }
  };

  if (!unlocked) {
    return (
      <section id="developer" className="chart-card settings-panel settings-panel-span-full">
        <div className="settings-panel-header">
          <div>
            <div className="team-card-heading">Developer Access</div>
            <div className="page-subtitle">
              API keys, webhook integration, and call-queue settings. Confirm your password to continue.
            </div>
          </div>
        </div>
        <form className="settings-fields" onSubmit={handleUnlock} style={{ maxWidth: 420 }}>
          <label className="settings-field">
            <span className="settings-label">Your account password</span>
            <input
              type="password"
              className="settings-input"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              placeholder="Enter your login password"
            />
          </label>
          {unlockError ? (
            <div className="muted-text" style={{ color: "#b91c1c", fontSize: 12 }} role="alert">
              {unlockError}
            </div>
          ) : null}
          <div className="page-actions">
            <Button type="submit" variant="gold" disabled={verifying || !password}>
              {verifying ? "Verifying..." : "Unlock Developer Access"}
            </Button>
          </div>
        </form>
      </section>
    );
  }

  return (
    <section id="developer" className="chart-card settings-panel settings-panel-span-full">
      <div className="settings-panel-header">
        <div>
          <div className="team-card-heading">Developer</div>
          <div className="page-subtitle">
            Permanent API keys for integrations (n8n, webhooks) and call-queue settings.
          </div>
        </div>
        <Button onClick={() => setUnlocked(false)}>Lock</Button>
      </div>

      {/* API KEYS */}
      <div style={{ marginTop: 12 }}>
        <div className="team-card-heading" style={{ fontSize: 15 }}>API Keys</div>
        <div className="page-subtitle">
          Use a key instead of a login token — it never expires.
        </div>

        {createdKey?.api_key ? (
          <div className="confirmation-tag success" style={{ display: "block", marginTop: 10 }}>
            <div style={{ marginBottom: 6 }}>
              New key <strong>{createdKey.name}</strong> — copy it now, it won't be shown again:
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <code style={{ wordBreak: "break-all", background: "rgba(0,0,0,0.06)", padding: "6px 8px", borderRadius: 6 }}>
                {createdKey.api_key}
              </code>
              <Button onClick={() => handleCopy(createdKey.api_key)}>Copy</Button>
              <Button onClick={() => setCreatedKey(null)}>Done</Button>
            </div>
          </div>
        ) : null}

        <form
          onSubmit={handleCreate}
          className="settings-fields"
          style={{ flexDirection: "row", gap: 8, alignItems: "flex-end", marginTop: 10 }}
        >
          <label className="settings-field" style={{ flex: 1, maxWidth: 320 }}>
            <span className="settings-label">New key name</span>
            <input
              className="settings-input"
              value={newKeyName}
              onChange={(event) => setNewKeyName(event.target.value)}
              placeholder="e.g. n8n-zoho"
            />
          </label>
          <Button type="submit" variant="green" disabled={creating || !newKeyName.trim()}>
            {creating ? "Creating..." : "Create key"}
          </Button>
        </form>

        <div style={{ marginTop: 12, overflowX: "auto" }}>
          <table className="data-table" style={{ width: "100%" }}>
            <thead>
              <tr><th>Name</th><th>Key</th><th>Status</th><th>Last used</th><th aria-label="actions" /></tr>
            </thead>
            <tbody>
              {keysLoading ? (
                <tr><td colSpan={5} className="muted-text">Loading…</td></tr>
              ) : keys.length ? (
                keys.map((k) => (
                  <tr key={k.api_key_id}>
                    <td>{k.name}</td>
                    <td><code>{k.masked_key || `${k.key_prefix}_••••••••`}</code></td>
                    <td>{k.status}</td>
                    <td>{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "never"}</td>
                    <td style={{ textAlign: "right" }}>
                      {k.status === "active" ? (
                        <Button variant="danger" onClick={() => handleRevoke(k.api_key_id)}>Revoke</Button>
                      ) : (
                        <span className="muted-text">revoked</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={5} className="muted-text">No API keys yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* QUEUE SETTINGS */}
      <div style={{ marginTop: 20 }}>
        <div className="team-card-heading" style={{ fontSize: 15 }}>Call Queue Settings</div>
        <div className="page-subtitle">
          Single calls dial immediately; bulk calls are queued and paced by these limits.
        </div>
        {queue ? (
          <>
            <div
              className="settings-fields"
              style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 10 }}
            >
              {QUEUE_FIELDS.map((field) => (
                <label key={field.key} className="settings-field">
                  <span className="settings-label">{field.label}</span>
                  <input
                    type="number"
                    className="settings-input"
                    min={field.min}
                    max={field.max}
                    value={queue[field.key] ?? ""}
                    onChange={(event) => setQueue((current) => ({ ...current, [field.key]: event.target.value }))}
                  />
                </label>
              ))}
              <label className="settings-field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={Boolean(queue.dry_run)}
                  onChange={(event) => setQueue((current) => ({ ...current, dry_run: event.target.checked }))}
                />
                <span className="settings-label" style={{ margin: 0 }}>Dry run (queue but don't dial)</span>
              </label>
            </div>
            <div className="page-actions" style={{ marginTop: 10 }}>
              <Button variant="gold" disabled={queueBusy} onClick={handleSaveQueue}>
                {queueBusy ? "Saving..." : "Save Queue Settings"}
              </Button>
            </div>
          </>
        ) : (
          <div className="muted-text">Loading queue settings…</div>
        )}
      </div>

      {/* WEBHOOK / USAGE */}
      <div style={{ marginTop: 20 }}>
        <div className="team-card-heading" style={{ fontSize: 15 }}>Webhook / Integration Usage</div>
        <div className="page-subtitle">Point n8n (or any webhook) at these endpoints using an API key above:</div>
        <ul style={{ marginTop: 8, lineHeight: 1.7 }}>
          <li><code>POST /api/calls/start</code> — single call</li>
          <li><code>POST /api/calls/bulk-start</code> — bulk (queued)</li>
          <li><code>POST /api/calls/&#123;call_id&#125;/cancel</code> — cancel</li>
        </ul>
        <div className="settings-inline-help" style={{ marginTop: 6 }}>
          Auth header: <code>Authorization: Bearer nxr_xxx</code> (or <code>x-api-key: nxr_xxx</code>).
          Unlike a login token, an API key does not expire after 24 hours.
        </div>
        <div style={{ marginTop: 12, display: "flex", gap: 16, flexWrap: "wrap" }}>
          <a href="/API-DOCS.html" target="_blank" rel="noreferrer">Open API reference ↗</a>
          <a href="/swagger.html" target="_blank" rel="noreferrer">Open Swagger UI ↗</a>
          <a href="/openapi.json" target="_blank" rel="noreferrer">Download OpenAPI spec ↗</a>
        </div>
      </div>
    </section>
  );
}
